import { Definition } from 'briefpoint-client';
import { DefinitionItem } from 'screens/DocumentGeneration/GenerateDiscoveryRequestPage';
import { punctuationRegexForDefAliases } from 'components/DocumentGeneration/DiscoveryRequests/Questions';
import raygun4js from 'raygun4js';

export const definitionRegex = new RegExp(/((?:[A-Z{}_]{3,}(?:\(S\))?(?:[ -]+[A-Z]{2,}(?:\(S\))?)*))/g);

export const excludeFromDefinitions = [
  'SSD',
  'PDA',
  'NASA',
  'NATO',
  'SWAT',
  'UNICEF',
  'UNESCO',
  'WHO',
  'OPEC',
  'FBI',
  'CIA',
  'LASER',
  'RADAR',
  'SCUBA',
  'VIP',
  'PTSD',
  'MRI',
  'DNA',
  'CPU',
  'RAM',
  'LCD',
  'LED',
  'URL',
  'HTTP',
  'HTML',
  'DUI',
  'DWI',
  'IMF',
  'GPA',
  'IBM',
  'ABS',
  'DVD',
  'USB',
  'PPE',
  'HIPAA',
  'SUV',
  'JPEG',
  'GIF',
  'SARS',
  'SIM',
  'ATM',
  'VPN',
  'DSLR',
  'DOB',
  'BCE',
  'AKA',
  'CEO',
  'CFO',
  'CPR',
  'CSI',
  'DMV',
  'FAQ',
  'FBI',
  'GDP',
  'GMO',
  'GPS',
  'HDTV',
  'ICU',
  'ISP',
  'LCD',
  'MBA',
  'NBA',
  'NFL',
  'RSVP',
  'USDA',
  'ZIP',
  'RFP',
  'STEM',
  'UFO',
  'VCR',
  'WI-FI',
  'LLC',
];

const escapeRegExp = (string: string) => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

export const definitionsUpdater = (
  updatedDef: Definition,
  ds: DefinitionItem[] | undefined,
  termToUpdate: string
) => {
  if (ds) {
    const existingIndex = ds.findIndex((x) => x.term === termToUpdate);
    let update = [...ds];

    if (existingIndex >= 0) {
      update[existingIndex] = updatedDef;
    } else {
      update.push(updatedDef);
    }
    return update;
  }
};

export const getUpdatedItems = (matches: DefinitionItem[], aliasToRemove: string) => {
  const normalizedTerm = aliasToRemove.toUpperCase();

  return matches.map((item) => {
    const updatedItem = { ...item };
    const aliasArray = updatedItem.aliases
      ?.split(/[,;]/)
      .map((alias) => alias.trim())
      .filter((alias) => alias);

    const filteredAliases = aliasArray?.filter((alias) => {
      const normalizedAlias = alias.toUpperCase();
      return normalizedAlias !== normalizedTerm;
    });

    updatedItem.aliases =
      filteredAliases && filteredAliases?.length > 0 ? filteredAliases?.join(', ') : undefined;

    return updatedItem;
  });
};

export const normalizeSpaces = (text: string) => {
  return text.replace(/\s+/g, ' ').trim();
};

function findWithin(text: string, searches?: string[]) {
  if (!text || !searches) return undefined;
  for (let i = 0; i < searches.length; i++) {
    const search = searches[i];

    if (text.includes(search)) {
      return search;
    }
  }

  return undefined;
}

export const getDefinitions = (
  text: string,
  existingDefinitions: DefinitionItem[],
  defs: Definition[],
  exclude: string[]
) => {
  const update = [...existingDefinitions];
  const missing = [];
  const definitionMatches = [...text.matchAll(definitionRegex)];
  const toExclude = excludeFromDefinitions.concat(exclude);

  if (!definitionMatches) return [];

  for (let m = 0; m < definitionMatches.length; m++) {
    let match = definitionMatches[m][0];

    //prevents matching on substitution strings
    if (match.startsWith('{') || toExclude.find((x) => x === match)) continue;

    for (let d = 0; d < defs.length; d++) {
      const definition = defs[d];
      if (!definition.term || definition.term.length < 3) {
        continue;
      }
      const termMatch = [definition.term];

      const parens = definition.term.indexOf('(');
      if (parens > -1) {
        try {
          // Just TERM of TERM(S)
          const singular = definition.term.substring(0, parens);
          termMatch.push(singular);
          // TERMS from TERM(S)
          const plural = definition.term.substring(parens + 1, definition.term.length - 1);
          termMatch.push(singular.concat(plural));
        } catch (e) {
          console.log(e);
          raygun4js('send', { error: e, message: `Term was: ${definition.term}` });
        }
      }
      let allSearches: string[] = [];
      try {
        allSearches = termMatch
          .concat(definition.aliases?.split(punctuationRegexForDefAliases)?.map((x) => x.trim()) ?? [])
          .sort((a, b) => b.length - a.length);
      } catch (e) {
        console.log(e);
        console.log(`This alias failed: ${definition.aliases}`);
      }
      const searchFound = findWithin(match, allSearches);
      // TODO: do we need to do (S) filtering on aliases as above?
      if (searchFound) {
        const existing = update.findIndex((x) => x.term === definition.term);
        // TODO: I can't figure out a clever way to not have duplicate code here
        if (existing === -1) {
          update.push(definition);
          //Need to potentially add definitions for terms inside other definitions
          const toAdd = getDefinitions(definition.content ?? '', update, defs, exclude) ?? [];
          toAdd.filter((a) => !update.find((u) => u.term === a.term)).forEach((a) => update.push(a));
        } else if (!update[existing].id) {
          update[existing] = definition;
          //Need to potentially add definitions for terms inside other definitions
          const toAdd = getDefinitions(definition.content ?? '', update, defs, exclude) ?? [];
          toAdd.filter((a) => !update.find((u) => u.term === a.term)).forEach((a) => update.push(a));
        }
        match = match.replace(searchFound, '').trim();

        if (match?.length < 3) {
          break;
        }
      }
    }

    if (match?.length >= 3) {
      missing.push({ term: match });
    }
  }

  return update.concat(missing);
};

interface TextItem {
  text: string;
  sourceId: string;
  [key: string]: any;
}

export const capitalizeTermsInText = (libItems: TextItem[], definitions: DefinitionItem[] | undefined) => {
  if (definitions) {
    const updatedTextArray: TextItem[] = JSON.parse(JSON.stringify(libItems));

    const termsToCheck: string[] = [];

    definitions.forEach((termObj: DefinitionItem) => {
      if (termObj.term) {
        termsToCheck.push(termObj.term);
      }

      if (termObj.aliases) {
        const aliasesList: string[] = termObj.aliases.split(',').map((alias) => alias.trim());
        termsToCheck.push(...aliasesList);
      }
    });

    // Process each text item
    updatedTextArray.forEach((textObj: TextItem) => {
      if (!textObj.text) return;

      let updatedText: string = textObj.text;

      // Check for each term/alias
      termsToCheck.forEach((term: string) => {
        if (!term) return;

        // Escape special regex characters
        const escapedTerm: string = escapeRegExp(term);

        // Match whole words only
        const regex: RegExp = new RegExp(`\\b${escapedTerm}\\b`, 'gi');

        // Replace with uppercase
        updatedText = updatedText.replace(regex, (match: string) => match.toUpperCase());
      });

      textObj.text = updatedText;
    });

    return updatedTextArray;
  }
};

export const capitalizeTermStr = (
  inputString: string | undefined,
  definitions: DefinitionItem[] | undefined
): string | undefined => {
  if (!inputString || !definitions?.length) return inputString;

  const allTerms: string[] = [];

  definitions.forEach((definition) => {
    if (definition.term) {
      allTerms.push(definition.term);
    }

    if (definition.aliases) {
      const aliasList = definition.aliases.split(',').map((alias) => alias.trim());
      allTerms.push(...aliasList);
    }
  });

  const sortedTerms = allTerms.filter((term) => !!term).sort((a, b) => b.length - a.length);

  let result = inputString;

  sortedTerms.forEach((term) => {
    const safePattern = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`\\b${safePattern}\\b`, 'gi');

    result = result.replace(regex, (match) => match.toUpperCase());
  });

  return result;
};
